import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Sun, Moon, ExternalLink, Save, Gift, Copy, Check, Users, Loader2, RefreshCw, Crown, DollarSign, ChartBar, HelpCircle } from 'lucide-react';
import { Button } from '../common/Button';
import { useToast } from '../common/Toast';
import { Link } from 'react-router-dom';
import ReferralStatsModal from './ReferralStatsModal';
import AmbassadorPaymentsModal from './AmbassadorPaymentsModal';
import ConnectWithUsModal from '../common/ConnectWithUsModal';
import { useAuth } from '../../contexts/AuthContext';
import referralService, { AmbassadorStatus, AmbassadorEarnings, PayoutInfo } from '../../services/referralService';
import userFeedbackService, { FeedbackCategory, FeedbackSubmissionResponse } from '../../services/userFeedbackService';

// Add this interface for tracking referral usage
interface ReferralUser {
  id: string;
  name: string;
  email: string;
  date: string;
  hasSubscribed: boolean;
  plan?: string;
  minutesEarned: number;
}

export const GeneralSettings: React.FC = () => {
  // Get the user data from the context
  const { user } = useAuth();
  
  // State for notification settings
  const [emailNotifications, setEmailNotifications] = useState<{
    interviews: boolean;
    feedback: boolean;
    subscription: boolean;
    marketing: boolean;
  }>({
    interviews: true,
    feedback: true,
    subscription: true,
    marketing: false
  });
  
  // State for feedback form
  const [feedback, setFeedback] = useState<string>('');
  const [feedbackCategory, setFeedbackCategory] = useState<string>('general');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState<boolean>(false);
  const [userHasFeedback, setUserHasFeedback] = useState<boolean>(false);
  const [loadingFeedbackStatus, setLoadingFeedbackStatus] = useState<boolean>(true);
  const [showFeedbackForm, setShowFeedbackForm] = useState<boolean>(true);
  const [rewardInfo, setRewardInfo] = useState<{ message: string; minutes: number } | null>(null);
  const [showRewardMessage, setShowRewardMessage] = useState<boolean>(false);
  
  // State for theme
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    // Check if user has set a theme preference in localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }
    // Check if user has set a system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const [themeSaved, setThemeSaved] = useState<boolean>(false);
  
  // State for referral code
  const [referralCode, setReferralCode] = useState<string>('');
  const [referralLink, setReferralLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [isLoadingReferral, setIsLoadingReferral] = useState<boolean>(false);
  
  // New state for the referral statistics modal
  const [showReferralStatsModal, setShowReferralStatsModal] = useState<boolean>(false);
  
  // State for referral statistics
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    activeSignups: 0,
    subscribed: 0,
    minutesEarned: 0,
  });
  
  // State for referred users
  const [referredUsers, setReferredUsers] = useState<ReferralUser[]>([]);

  // New ambassador-related state
  const [ambassadorStatus, setAmbassadorStatus] = useState<AmbassadorStatus>({
    isAmbassador: false,
    status: null,
    application: null
  });
  const [ambassadorEarnings, setAmbassadorEarnings] = useState<AmbassadorEarnings>({
    totalEarnings: 0,
    pendingEarnings: 0,
    totalPayouts: 0,
    totalReferrals: 0,
    successfulReferrals: 0
  });
  const [ambassadorPayouts, setAmbassadorPayouts] = useState<PayoutInfo[]>([]);
  const [showAmbassadorPaymentsModal, setShowAmbassadorPaymentsModal] = useState<boolean>(false);
  const [isLoadingAmbassador, setIsLoadingAmbassador] = useState<boolean>(false);
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false);
  
  const { showToast } = useToast();
  
  // Extract fetchReferralInfo to be used by both useEffect and refresh button
  const fetchReferralInfo = async () => {
    return;
    console.log('fetchReferralInfo: Fetching referral info...');
    try {
      setIsLoadingReferral(true);
      console.log('fetchReferralInfo: Set loading to true');
      
      // Get user's referral code
      console.log('fetchReferralInfo: Fetching referral code...');
      const code = await referralService.getMyReferralCode();

      if (code) {
      console.log('fetchReferralInfo: Referral code received:', code);
      setReferralCode(code);
      setReferralLink(`${window.location.origin}/signup?ref=${code}`);
      } else {
        setReferralCode('');
        setReferralLink('');
      }
      
      // Get referral statistics
      console.log('fetchReferralInfo: Fetching referral statistics...');
      const stats = await referralService.getReferralStatistics();
      console.log('fetchReferralInfo: Referral statistics received:', stats);
      setReferralStats({
        totalReferrals: stats.totalReferrals,
        activeSignups: stats.activeSignups,
        subscribed: stats.subscribed,
        minutesEarned: stats.minutesEarned
      });
      
      // Get referral history for the latest referred users
      console.log('fetchReferralInfo: Fetching referral history...');
      const history = await referralService.getReferralHistory({
        limit: 6,
        page: 1
      });
      console.log('fetchReferralInfo: Referral history received:', history);
      
      setReferredUsers(history.data);
      console.log('fetchReferralInfo: Successfully completed referral info fetch');
    } catch (error) {
      console.error('fetchReferralInfo: Error fetching referral information:', error);
      
      // Use a default referral code in case of error
      if (!referralCode) {
        console.log('fetchReferralInfo: No existing referral code, generating default...');
        setReferralCode('');
        setReferralLink('');
        console.log('fetchReferralInfo: Set default referral code:', referralCode);
      }
      showToast('Failed to load referral data. Please try refreshing.', 'error');
      throw error; // Re-throw so the refresh handler can catch it
    } finally {
      console.log('fetchReferralInfo: Setting loading to false');
      setIsLoadingReferral(false);
    }
  };

  // New function to fetch ambassador information
  const fetchAmbassadorInfo = async () => {
    return;
    try {
      setIsLoadingAmbassador(true);
      console.log('fetchAmbassadorInfo: Set loading to true');
      
      // Get ambassador status
      console.log('fetchAmbassadorInfo: Fetching ambassador status...');
      const status = await referralService.getAmbassadorStatus();
      console.log('fetchAmbassadorInfo: Ambassador status received:', status);
      setAmbassadorStatus(status);
      
      // If user is an ambassador, fetch earnings and payouts
      if (status.isAmbassador && status.status === 'approved') {
        console.log('fetchAmbassadorInfo: User is approved ambassador, fetching earnings and payouts...');
        try {
          const [earnings, payouts] = await Promise.all([
            referralService.getAmbassadorEarnings(),
            referralService.getAmbassadorPayouts()
          ]);
          
          console.log('fetchAmbassadorInfo: Ambassador earnings received:', earnings);
          console.log('fetchAmbassadorInfo: Ambassador payouts received:', payouts);
          
          setAmbassadorEarnings(earnings);
          setAmbassadorPayouts(payouts.payouts);
        } catch (error) {
          console.warn('fetchAmbassadorInfo: Error fetching ambassador earnings/payouts:', error);
          // Don't fail if earnings/payouts fail, just log the warning
        }
      } else {
        console.log('fetchAmbassadorInfo: User is not an approved ambassador, skipping earnings/payouts');
      }
      
      console.log('fetchAmbassadorInfo: Successfully completed ambassador info fetch');
    } catch (error) {
      console.error('fetchAmbassadorInfo: Error fetching ambassador information:', error);
      // Don't show error toast for ambassador info as it's optional
    } finally {
      console.log('fetchAmbassadorInfo: Setting loading to false');
      setIsLoadingAmbassador(false);
    }
  };
  
  // Fetch referral information when component mounts
  useEffect(() => {
    console.log('GeneralSettings useEffect: Component mounted, calling fetch functions...');
    fetchReferralInfo();
    fetchAmbassadorInfo();
  }, []);
  
  // Check if user has already submitted feedback
  useEffect(() => {
    const checkUserFeedback = async () => {
        setLoadingFeedbackStatus(true);
        try {
          const hasFeedback = await userFeedbackService.hasFeedback();
          setUserHasFeedback(hasFeedback);
          setShowFeedbackForm(!hasFeedback);
        } catch (error) {
          console.error('Error checking user feedback status:', error);
        } finally {
          setLoadingFeedbackStatus(false);
        }
    };
    
    checkUserFeedback();
  }, []);
  
  // Handle notification toggle change
  const handleNotificationChange = (key: keyof typeof emailNotifications) => {
    setEmailNotifications({
      ...emailNotifications,
      [key]: !emailNotifications[key]
    });
  };
  
  // Handle feedback submission
  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedbackSubmitting(true);
    
    try {
      const response = await userFeedbackService.submitFeedback({
        content: feedback,
        category: feedbackCategory as FeedbackCategory
      }) as FeedbackSubmissionResponse;
      
      setFeedback('');
      setUserHasFeedback(true);
      setShowFeedbackForm(false);
      
      // Check if user received a reward
      if (response.rewardInfo?.isFirstFeedback && response.rewardInfo.rewardMinutes && response.rewardInfo.message) {
        setRewardInfo({
          message: response.rewardInfo.message,
          minutes: response.rewardInfo.rewardMinutes
        });
        setShowRewardMessage(true);
        showToast(`Thank you for your feedback! You've earned ${response.rewardInfo.rewardMinutes} free minutes!`, 'success');
        
        // Hide reward message after 8 seconds
        setTimeout(() => {
          setShowRewardMessage(false);
        }, 8000);
      } else {
        showToast('Thank you for your feedback!', 'success');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showToast('Failed to submit feedback. Please try again.', 'error');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Handle showing the feedback form again
  const handleSubmitAnotherFeedback = () => {
    setShowFeedbackForm(true);
    setShowRewardMessage(false);
    setRewardInfo(null);
  };
  
  // Handle theme change
  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    
    // Apply theme but don't save yet
    if (newTheme === 'system') {
      // Apply system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        // Apply dark mode for system dark preference
        document.documentElement.classList.add('dark');
        console.log("System theme: dark mode - added 'dark' class");
      } else {
        // Apply light mode for system light preference
        document.documentElement.classList.remove('dark');
        console.log("System theme: light mode - removed 'dark' class");
      }
    } else if (newTheme === 'dark') {
      // Apply dark mode directly
      document.documentElement.classList.add('dark');
      console.log("Theme preview: dark mode - added 'dark' class");
    } else {
      // Apply light mode directly
      document.documentElement.classList.remove('dark');
      console.log("Theme preview: light mode - removed 'dark' class");
    }
    localStorage.setItem('theme', newTheme);
  };

  // Handle save theme setting
  const handleSaveTheme = () => {
    // Update localStorage and apply theme
    if (theme === 'system') {
      localStorage.removeItem('theme');
      console.log("Theme saved: system preference (removed from localStorage)");
      
      // Apply system preference immediately
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark');
        console.log("Applied system dark theme - added 'dark' class");
      } else {
        document.documentElement.classList.remove('dark');
        console.log("Applied system light theme - removed 'dark' class");
      }
    } else {
      // Save theme preference to localStorage
      localStorage.setItem('theme', theme);
      console.log(`Saved theme '${theme}' to localStorage`);
      
      // Apply theme immediately
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
        console.log("Applied dark theme - added 'dark' class");
      } else {
        document.documentElement.classList.remove('dark');
        console.log("Applied light theme - removed 'dark' class");
      }
    }

    // Show saved confirmation
    setThemeSaved(true);
    
    setTimeout(() => {
      setThemeSaved(false);
    }, 3000);
  };
  
  // Format date function for referral stats
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  
  // Handle referral link copy
  const handleCopyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setLinkCopied(true);
    showToast('Referral link copied to clipboard!', 'success');
    
    setTimeout(() => {
      setLinkCopied(false);
    }, 3000);
  };
  
  // Handle opening referral stats modal
  const handleOpenReferralStats = async () => {
    try {
      // Refresh referral history when opening the modal
      const history = await referralService.getReferralHistory({
        limit: 10,
        page: 1
      });
      
      setReferredUsers(history.data);
      setShowReferralStatsModal(true);
    } catch (error) {
      console.error('Error fetching updated referral history:', error);
      showToast('Failed to fetch latest referral data. Please try again.', 'error');
    }
  };
  
  // Handle closing referral stats modal
  const handleCloseReferralStats = () => {
    setShowReferralStatsModal(false);
  };

  // Handle opening ambassador payments modal
  const handleOpenAmbassadorPayments = async () => {
    try {
      setIsLoadingAmbassador(true);
      // Refresh ambassador data when opening the modal
      const [earnings, payouts] = await Promise.all([
        referralService.getAmbassadorEarnings(),
        referralService.getAmbassadorPayouts()
      ]);
      
      setAmbassadorEarnings(earnings);
      setAmbassadorPayouts(payouts.payouts);
      setShowAmbassadorPaymentsModal(true);
    } catch (error) {
      console.error('Error fetching updated ambassador data:', error);
      showToast('Failed to fetch latest payment data. Please try again.', 'error');
    } finally {
      setIsLoadingAmbassador(false);
    }
  };

  // Handle closing ambassador payments modal
  const handleCloseAmbassadorPayments = () => {
    setShowAmbassadorPaymentsModal(false);
  };
  
  return (
    <>
      <div className="space-y-12">
        {/* Referral Code Section */}
        {/* <div>
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2 md:gap-3 flex-col md:flex-row">
              <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-3">Refer & Earn</h2> */}
              
              {/* Ambassador Badge */}
              {/* {ambassadorStatus.isAmbassador && ambassadorStatus.status === 'approved' ? (
                <div className="flex items-center gap-2 px-3 py-1 -mt-3 md:mt-0 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/20 rounded-full">
                  <Crown size={15} className="text-amber-600 dark:text-amber-400" />
                  <span className="text-xs md:text-sm font-medium text-amber-700 dark:text-amber-300">Ambassador</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 -mt-3 md:mt-0 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/20 rounded-full hover:cursor-pointer hover:bg-yellow-500/20 dark:hover:bg-yellow-500/20 transition-colors duration-600" onClick={(e) => {
                  window.open('https://mocktrail.app/ambassador-program?utm_source=mocktrail-app&utm_medium=referral&utm_campaign=ambassador-program', '_blank');
                }}>
                  <Crown size={15} className="text-yellow-600 dark:text-yellow-400" />
                  <span className="text-xs md:text-sm font-medium text-yellow-700 dark:text-yellow-300">Become an Ambassador</span>
                </div>
              )} */}
            {/* </div>
            
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                console.log('Refresh button clicked, starting data refresh...');
                setIsLoadingReferral(true);
                
                try {
                  console.log('Fetching referral and ambassador information...');
                  
                  const promises = [
                    fetchReferralInfo().catch(err => {
                      console.error('fetchReferralInfo failed:', err);
                      throw err;
                    }),
                    fetchAmbassadorInfo().catch(err => {
                      console.error('fetchAmbassadorInfo failed:', err);
                      // Don't throw ambassador errors as they're optional
                      console.warn('Ambassador info fetch failed, continuing...');
                    })
                  ];
                  
                  await Promise.allSettled(promises);
                  console.log('Data refresh completed successfully');
                  showToast('Information refreshed', 'success');
                } catch (error) {
                  console.error('Error during refresh:', error);
                  showToast('Failed to refresh data', 'error');
                } finally {
                  console.log('Refresh operation finished, setting loading to false');
                  setIsLoadingReferral(false);
                }
              }}
              leftIcon={<RefreshCw size={16} className={isLoadingReferral ? 'animate-spin' : ''} />}
              loading={isLoadingReferral}
            >
              Refresh
            </Button>
          </div> */}
{/*           
          <div className="mt-6 md:mt-0">
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <Gift className="mr-2 text-primary-light dark:text-primary-dark" size={16} />
              Invite Friends & Earn Benefits
            </h3>
            
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
              Share your referral code with friends. When they sign up, you'll both receive 20 minutes of free interview time.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Share your referral link</label>
                <div className="form-field-container">
                  <input 
                    type="text" 
                    value={referralLink || 'Couldn\'t load referral link. Please refresh to try again.'} 
                    readOnly
                    className="py-3 px-4 flex-grow font-medium font-heading text-base text-text-primary-light dark:text-text-primary-dark"
                    disabled={isLoadingReferral}
                  />
                  <button 
                    onClick={handleCopyReferralLink} 
                    className="px-4 py-3 text-base bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark hover:bg-primary-light/20 dark:hover:bg-primary-dark/20 transition-colors rounded-r-lg flex items-center"
                    disabled={isLoadingReferral}
                  >
                    {linkCopied ? <Check size={18} /> : <Copy size={18} />}
                    <span className="ml-2 text-base font-medium ">{linkCopied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>
              
              <div className="flex justify-between flex-col md:flex-row space-y-4 md:space-y-0">
                <div className="flex items-center space-x-3">
                  <div className="bg-primary-light/10 dark:bg-primary-dark/10 p-2 rounded-full">
                    <Users size={18} className="text-primary-light dark:text-primary-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                      {referralStats.totalReferrals} users signed up
                    </p>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      {referralStats.subscribed} subscribed
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 w-full md:w-auto">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleOpenReferralStats}
                    className="font-medium w-full md:w-auto"
                    leftIcon={<ChartBar size={14} />}
                  >
                    View Statistics
                  </Button>
                   */}
                  {/* Show Ambassador Payments button only for approved ambassadors */}
                  {/* {ambassadorStatus.isAmbassador && ambassadorStatus.status === 'approved' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleOpenAmbassadorPayments}
                      className="font-medium w-full md:w-auto"
                      leftIcon={<DollarSign size={14} />}
                    >
                      View Payments
                    </Button>
                  )} */}
                {/* </div> */}
              {/* </div> */}
              
              {/* <div className="mt-3 p-4 rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
                <h4 className="font-medium mb-2 text-text-primary-light dark:text-text-primary-dark">
                  How it works
                </h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Share your unique code with friends
                  </li>
                  <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    When they signup using your code, you get 20 minutes of interview time.
                  </li>
                  <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    When they subscribe to a plan, you get 40 minutes of free interview time.
                  </li>
                  {ambassadorStatus.isAmbassador && ambassadorStatus.status === 'approved' ? (
                    <li className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                      💰 As an ambassador, you earn {process.env.REACT_APP_AMBASSADOR_PROGRAM_REFERRAL_PERCENTAGE || "10"}% commission on the referrals who subscribe to a plan!
                    </li>
                  ) : (
                    <li className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                      Become an ambassador to earn real money with your referral code!
                    </li>
                  )}
                  <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Invite unlimited friends and earn more rewards
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div> */}
        
        {/* Theme Settings*/}
        <div>
          <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-3">Appearance</h2>
          
          <div className="">
            <h3 className="text-lg font-medium mb-4">Theme</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <button
                onClick={() => handleThemeChange('light')}
                className={`p-3 rounded-lg border transition-all duration-200 flex flex-col md:flex-row md:items-center ${
                  theme === 'light'
                    ? 'border-primary-light dark:border-primary-dark bg-primary-light/5 dark:bg-primary-dark/5'
                    : 'border-border-light dark:border-border-dark hover:border-primary-light/30 dark:hover:border-primary-dark/30'
                }`}
              >
                <Sun size={18} className="mr-3 text-amber-500 my-1 md:my-0" />
                <div className="text-left">
                  <span className="font-medium block text-base">Light Mode</span>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark leading-tight mt-1 mb-1">Light background with dark text</p>
                </div>
              </button>
              
              <button
                onClick={() => handleThemeChange('dark')}
                className={`p-3 rounded-lg border transition-all duration-200 flex flex-col md:flex-row md:items-center ${
                  theme === 'dark'
                    ? 'border-primary-light dark:border-primary-dark bg-primary-light/5 dark:bg-primary-dark/5'
                    : 'border-border-light dark:border-border-dark hover:border-primary-light/30 dark:hover:border-primary-dark/30'
                }`}
              >
                <Moon size={18} className="mr-3 text-indigo-400 my-1 md:my-0" />
                <div className="text-left">
                  <span className="font-medium block text-base">Dark Mode</span>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark leading-tight mt-1 mb-1">Dark background with light text</p>
                </div>
              </button>
            </div>

            <div className="flex justify-between items-center">
            </div>
          </div>
        </div>

        {/* Notification Settings, Keeping it hidden for now */}
        <div className="hidden">
          <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-3">Notification Settings</h2>
          
          <div className="space-y-4">
            <div className="">
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <Mail className="mr-2 text-primary-light dark:text-primary-dark" size={16} />
                Email Notifications
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-base">Interview Reminders</h4>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      Receive reminders before your scheduled interviews
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={emailNotifications.interviews} 
                      onChange={() => handleNotificationChange('interviews')} 
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-surface-light dark:bg-surface-dark peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-light/30 dark:peer-focus:ring-primary-dark/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-light dark:peer-checked:bg-primary-dark"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between pt-3 border-t border-border-light/20 dark:border-border-dark/20">
                  <div>
                    <h4 className="font-medium text-base">Interview Feedback</h4>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      Get notified when your interview feedback is ready
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={emailNotifications.feedback} 
                      onChange={() => handleNotificationChange('feedback')} 
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-surface-light dark:bg-surface-dark peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-light/30 dark:peer-focus:ring-primary-dark/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-light dark:peer-checked:bg-primary-dark"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between pt-3 border-t border-border-light/20 dark:border-border-dark/20">
                  <div>
                    <h4 className="font-medium text-base">Subscription Updates</h4>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      Updates about your subscription plan, renewals, and payments
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={emailNotifications.subscription} 
                      onChange={() => handleNotificationChange('subscription')} 
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-surface-light dark:bg-surface-dark peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-light/30 dark:peer-focus:ring-primary-dark/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-light dark:peer-checked:bg-primary-dark"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between pt-3 border-t border-border-light/20 dark:border-border-dark/20">
                  <div>
                    <h4 className="font-medium text-base">Marketing & Promotions</h4>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      Product tips, special offers, and promotional messages
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={emailNotifications.marketing} 
                      onChange={() => handleNotificationChange('marketing')} 
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-surface-light dark:bg-surface-dark peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-light/30 dark:peer-focus:ring-primary-dark/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-light dark:peer-checked:bg-primary-dark"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback Section */}
        <div>
          <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mt-4 mb-3">Send Feedback</h2>
          
          <div className="">
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <MessageSquare className="mr-2 text-primary-light dark:text-primary-dark" size={16} />
              Help Us Improve
            </h3>
                
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-3">
              We'd love to hear your thoughts on how we can make MockTrail better. You get <span className="font-semibold text-text-primary-light dark:text-text-primary-dark"> {process.env.REACT_APP_FEEDBACK_FREE_MINUTES || "10"} minutes of interview free</span> for one-time on giving feedback.
            </p>
            
            {loadingFeedbackStatus ? (
              <div className="flex justify-center py-6">
                 <div className="text-center py-4">
                  <Loader2 size={24} className="animate-spin mx-auto text-primary-light dark:text-primary-dark mb-2" />
                  <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                    Loading feedback...
                  </p>
                </div>
              </div>
            ) : showFeedbackForm ? (
              <form onSubmit={handleFeedbackSubmit}>
                <div className="mb-3">
                  <label className="block text-sm font-medium mb-1">Feedback Category</label>
                  <select
                    value={feedbackCategory}
                    onChange={(e) => setFeedbackCategory(e.target.value)}
                    className="w-full bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg px-3 py-2 text-base text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-light/30 dark:focus:ring-primary-dark/30"
                  >
                    <option value="general">General Feedback</option>
                    <option value="interview">Interview Experience</option>
                    <option value="usability">Usability</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                  </select>
                </div>
                
                <div className="mb-3">
                  <label className="block text-sm font-medium mb-1">Your Feedback</label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    className="w-full bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg px-3 py-3 text-base text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-light/30 dark:focus:ring-primary-dark/30"
                    placeholder="Share your thoughts, suggestions, or report issues..."
                    rows={5}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Button
                    variant="primary"
                    size="md"
                    type="submit"
                    loading={feedbackSubmitting}
                    disabled={!feedback.trim() || feedbackSubmitting}
                  >
                    Submit Feedback
                  </Button>
                </div>
              </form>
            ) : (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6 mb-4">
                <div className="flex flex-col items-center text-center">
                  <Check size={48} className="text-green-500 mb-3" />
                  <h4 className="text-lg font-bold mb-2">Thank You for Your Feedback!</h4>
                  
                  {/* Reward Message */}
                  {showRewardMessage && rewardInfo && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border border-green-200 dark:border-green-700">
                      <div className="flex items-center justify-center mb-2">
                        <Gift className="w-6 h-6 text-green-500 mr-2" />
                        <span className="font-semibold text-green-700 dark:text-green-300">
                          +{rewardInfo.minutes} Free Minutes Earned!
                        </span>
                      </div>
                      <p className="text-sm text-center text-green-600 dark:text-green-400">
                        {rewardInfo.message}
                      </p>
                    </div>
                  )}
                  
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
                    We appreciate you taking the time to share your thoughts. Your feedback helps us improve MockTrail for everyone.
                  </p>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={handleSubmitAnotherFeedback}
                  >
                    Submit Another Feedback
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-2 justify-between w-full border-t border-border-light/20 dark:border-border-dark/20">

        <div className="flex flex-col md:flex-row gap-2">
          <Link to="/privacy">
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              rightIcon={<ExternalLink size={14} />}
            >
              View Privacy Policy
            </Button>
          </Link>
          <Link to="/terms">
            <Button
              variant="secondary"
              size="sm" 
              className="mt-4"
              rightIcon={<ExternalLink size={14} />}
            >
              View Terms of Service
            </Button>
            </Link>
            {/* <Button
              variant="secondary"
              size="sm" 
              className="mt-4"
              rightIcon={<HelpCircle size={14} />}
              onClick={() => setShowConnectModal(true)}
            >
              Need Help? Connect with us
            </Button> */}
          </div>
          {/* Made in India */}
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark flex items-center gap-1 mt-4 md:mt-1">
              <img src={"https://upload.wikimedia.org/wikipedia/commons/b/bc/Flag_of_India.png"} alt="Made in India" className="w-4 h-3 inline-block mr-1" />
              <span className="text-sm text-orange-500">Made</span> <span className="text-sm text-text-primary-light dark:text-text-primary-dark">in</span> <span className="text-sm text-green-500">India</span>
            </p>
        </div>
      </div>
      
      {/* Using the new ReferralStatsModal component */}
      <ReferralStatsModal 
        isOpen={showReferralStatsModal}
        onClose={handleCloseReferralStats}
        stats={{
          totalReferrals: referralStats.totalReferrals,
          subscribed: referralStats.subscribed,
          minutesEarned: referralStats.minutesEarned
        }}
        referredUsers={referredUsers}
        formatDate={formatDate}
      />

      {/* Using the new AmbassadorPaymentsModal component */}
      <AmbassadorPaymentsModal 
        isOpen={showAmbassadorPaymentsModal}
        onClose={handleCloseAmbassadorPayments}
        earnings={ambassadorEarnings}
        payouts={ambassadorPayouts}
      />

      {/* Connect With Us Modal */}
      <ConnectWithUsModal 
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
      />
    </>
  );
}; 